import { useEffect, useMemo, useState, useCallback } from 'react';

/**
 * ViewModel for Tabs component.
 * @param {{ tabs: Array<{id:string,label:string}>, activeIndex?: number, onChange?: (index:number)=>void }} options - Options.
 */
export function useViewModel({ tabs = [], activeIndex = 0, onChange }) {
  const isControlled = typeof activeIndex === 'number';
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0);

  useEffect(() => {
    // Ensure active index is in range when tabs change.
    if (isControlled) return;
    if (uncontrolledIndex >= tabs.length) {
      setUncontrolledIndex(0);
    }
  }, [tabs.length, uncontrolledIndex, isControlled]);

  const currentIndex = isControlled
    ? Math.min(Math.max(activeIndex, 0), Math.max(0, tabs.length - 1))
    : uncontrolledIndex;

  const setIndex = useCallback(
    (index) => {
      const clamped = Math.min(Math.max(index, 0), Math.max(0, tabs.length - 1));
      if (isControlled) {
        onChange && onChange(clamped);
      } else {
        setUncontrolledIndex(clamped);
        onChange && onChange(clamped);
      }
    },
    [isControlled, tabs.length, onChange]
  );

  const handleClick = useCallback((index) => setIndex(index), [setIndex]);

  const handleKeyDown = useCallback(
    (e, index) => {
      // Left/Right arrow navigation.
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex((index + 1) % tabs.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((index - 1 + tabs.length) % tabs.length);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setIndex(tabs.length - 1);
      }
    },
    [tabs.length, setIndex]
  );

  const getTabId = useCallback((index) => `tabs-tab-${index}`, []);
  const getPanelId = useCallback((index) => `tabs-panel-${index}`, []);

  return {
    currentIndex,
    handleClick,
    handleKeyDown,
    getTabId,
    getPanelId
  };
}
